import { createSelector, createSlice, current } from "@reduxjs/toolkit";

export const actualSlice = createSlice({
    name: "actual",
    initialState: {
        totals: {},
        actualLines: {},
        billLines: {},
        purchaseOrders: {},

        billLinesPopupOpen: false,
        linesPopupOpen: false,
        linePopupOpen: false,

        tab: 0,

        toAllocateBillLines: [],
        toAllocateLines: [],
        allocations: [],
        searchedLineIdsSet: null,
        searchedBillLineIdsSet: null,

        deallocatingBillLine: null,
        refutingBillLine: null,
        confirmingRefutedBillLine: null,

        showRunning: false,

        editingBillLine: null,
        editingPurchaseOrder: null,

        taxCodes: {},
        accountingAccounts: {},

        projectDocket: null,
    },

    reducers: {
        updateTotals: (state, action) => {
            const {
                billed,
                total_quoted,
                total_budget,
                internal_budget,
                external_budget,
                external_budget_running,
                gross_profit,
                currency,
                float_internal_scheduled,
                float_internal_actual,
            } = action.payload;

            state.totals = {
                billed,
                total_quoted,
                total_budget,
                internal_budget,
                external_budget,
                external_budget_running,
                gross_profit,
                currency,
                float_internal_scheduled,
                float_internal_actual,
            };
        },
        updateLines: (state, action) => {
            const lines = action.payload;
            state.actualLines = lines.reduce(
                (o, line) => ({ ...o, [line.id]: { ...line } }),
                {},
            );
        },
        updateLine: (state, action) => {
            const line = action.payload;
            state.actualLines[line.id] = line;
        },
        deleteLine: (state, action) => {
            const lineId = action.payload;
            delete state.actualLines[lineId];
        },
        updateBillLines: (state, action) => {
            const billLines = action.payload;
            state.billLines = billLines.reduce(
                (o, billLine) => ({ ...o, [billLine.id]: { ...billLine } }),
                {},
            );
        },
        updateBillLine: (state, action) => {
            const billLine = action.payload;
            state.billLines[billLine.id] = billLine;
        },
        updatePurchaseOrders: (state, action) => {
            const purchaseOrders = action.payload;
            state.purchaseOrders = purchaseOrders.reduce(
                (o, purchaseOrder) => ({
                    ...o,
                    [purchaseOrder.id]: { ...purchaseOrder },
                }),
                {},
            );
        },
        updatePurchaseOrder: (state, action) => {
            const purchaseOrder = action.payload;
            state.purchaseOrders[purchaseOrder.id] = purchaseOrder;
        },
        deletePurchaseOrder: (state, action) => {
            const purchaseOrderId = action.payload;
            delete state.purchaseOrders[purchaseOrderId];
        },
        setEditingPurchaseOrder: (state, action) => {
            state.editingPurchaseOrder = action.payload;
        },
        openBillLinesPopup: (state) => {
            state.billLinesPopupOpen = true;
        },
        closeBillLinesPopup: (state) => {
            state.billLinesPopupOpen = false;
        },
        openLinesPopup: (state) => {
            state.linesPopupOpen = true;
        },
        closeLinesPopup: (state) => {
            state.linesPopupOpen = false;
        },
        openLinePopup: (state) => {
            state.linePopupOpen = true;
        },
        closeLinePopup: (state) => {
            state.linePopupOpen = false;
        },
        switchTab: (state, action) => {
            state.tab = action.payload;
        },
        checkBillLine: (state, action) => {
            const billLine = state.billLines[action.payload];
            console.log(billLine);
            const canMultiSelect = state.toAllocateLines.length <= 1;
            if (canMultiSelect) {
                state.toAllocateBillLines.push(billLine);
            } else {
                state.toAllocateBillLines = [billLine];
            }
        },
        checkLine: (state, action) => {
            const line = state.actualLines[action.payload];
            const canMultiSelect = state.toAllocateBillLines.length <= 1;
            if (canMultiSelect) {
                state.toAllocateLines.push(line);
            } else {
                state.toAllocateLines = [line];
            }
        },
        uncheckBillLine: (state, action) => {
            const billLineId = action.payload;
            state.toAllocateBillLines = state.toAllocateBillLines.filter(
                (billLine) => billLine.id != billLineId,
            );
            state.allocations = state.allocations.filter(
                (allocation) =>
                    allocation.accounting_bill_line_id != billLineId,
            );
        },
        uncheckLine: (state, action) => {
            const lineId = action.payload;
            state.toAllocateLines = state.toAllocateLines.filter(
                (line) => line.id != lineId,
            );
            state.allocations = state.allocations.filter(
                (allocation) => allocation.actual_line_id != lineId,
            );
        },
        startAllocation: (state) => {
            const toAllocateLines = state.toAllocateLines;
            const toAllocateBillLines = state.toAllocateBillLines;
            const allocations = state.allocations;
            console.log(
                Array.from(state.toAllocateLines),
                state.toAllocateBillLines,
            );

            const multiLineMode =
                toAllocateLines.length > 1 && toAllocateBillLines.length == 1;
            const oneLineMode =
                toAllocateLines.length == 1 && toAllocateBillLines.length >= 1;

            if (multiLineMode) {
                toAllocateLines.forEach((line) => {
                    allocations.push({
                        actual_line_id: line.id,
                        accounting_bill_line_id: toAllocateBillLines[0].id,
                        actual: 0,
                    });
                });
            } else if (oneLineMode) {
                toAllocateBillLines.forEach((billLine) => {
                    allocations.push({
                        actual_line_id: toAllocateLines[0].id,
                        accounting_bill_line_id: billLine.id,
                        actual: billLine.amount,
                    });
                });
            } else {
                throw "Invalid line and bill line combinations";
            }
        },
        stopAllocation: (state) => {
            state.allocations = [];
        },
        editAllocation: (state, action) => {
            const { model_id, model_type_id_key, data } = action.payload;
            state.allocations = state.allocations.map((allocation) =>
                allocation[model_type_id_key] == model_id
                    ? { ...allocation, ...data }
                    : allocation,
            );
        },
        resetAllocations: (state) => {
            state.allocations = [];
        },
        resetAll: (state) => {
            state.toAllocateBillLines = [];
            state.toAllocateLines = [];
            state.allocations = [];
        },
        distributeRemaining: (state) => {
            let workingBillLine = null;
            if (
                state.toAllocateBillLines.length == 1 &&
                state.toAllocateLines.length > 1
            ) {
                workingBillLine = state.toAllocateBillLines[0];
            }
            const allocationsToDistribute = state.allocations.filter(
                (line) => line.actual == 0,
            ).length;
            const allocateTotal = state.allocations.reduce(
                (total, allocation) => total + allocation.actual,
                0,
            );
            const remainingAmount = workingBillLine.amount - allocateTotal;

            let remainderIncluded = false;
            const portion =
                Math.floor((remainingAmount * 100) / allocationsToDistribute) /
                100;
            const remainder =
                ((remainingAmount * 100) % allocationsToDistribute) / 100;
            state.allocations = state.allocations.map((allocation) => {
                const notAllocated =
                    parseFloat(allocation.actual).toFixed(2) == 0;
                const allocateAmount =
                    portion + (!remainderIncluded ? remainder : 0);
                const newAllocations = notAllocated
                    ? { ...allocation, actual: allocateAmount.toFixed(2) }
                    : allocation;
                if (!remainderIncluded && notAllocated) {
                    remainderIncluded = true;
                }
                return newAllocations;
            });
        },
        searchLines: (state, action) => {
            const searchString = action.payload;
            if (!searchString) {
                state.searchedLineIdsSet = null;
            }
            const searchedLines = Object.values(state.actualLines).filter(
                (line) => line.name.toLowerCase().includes(searchString),
            );
            state.searchedLineIdsSet = Object.fromEntries(
                searchedLines.map((line) => [line.id, true]),
            );
        },
        searchBillLines: (state, action) => {
            const searchString = action.payload;
            if (!searchString) {
                state.searchedBillLineIdsSet = null;
            }
            const searchedBillLines = Object.values(state.billLines).filter(
                (billLine) =>
                    billLine.vendor_name.toLowerCase().includes(searchString),
            );
            state.searchedBillLineIdsSet = Object.fromEntries(
                searchedBillLines.map((billLine) => [billLine.id, true]),
            );
        },
        setDeallocatingBillLine: (state, action) => {
            state.deallocatingBillLine = action.payload;
        },
        cancelDeallocatingBillLine: (state, action) => {
            state.deallocatingBillLine = null;
        },
        setRefutingBillLine: (state, action) => {
            state.refutingBillLine = action.payload;
        },
        cancelRefutingBillLine: (state, action) => {
            state.refutingBillLine = null;
        },
        setConfirmingRefutedBillLine: (state, action) => {
            state.confirmingRefutedBillLine = action.payload;
        },
        cancelConfirmingRefutedBillLine: (state, action) => {
            state.confirmingRefutedBillLine = null;
        },
        toggleRunning: (state, action) => {
            state.showRunning = !state.showRunning;
        },
        setTaxCodes: (state, action) => {
            state.taxCodes = action.payload;
        },
        setAccountingAccounts: (state, action) => {
            state.accountingAccounts = action.payload;
        },
        addAccountingAccount: (state, action) => {
            const { legalEntityId, accountingAccount } = action.payload;
            state.accountingAccounts[legalEntityId].push(accountingAccount);
        },
        updateProjectDocket: (state, action) => {
            state.projectDocket = action.payload;
        },
    },
});

export const {
    updateTotals,
    updateLines,
    updateLine,
    deleteLine,
    updateBillLines,
    updateBillLine,
    checkBillLine,
    checkLine,
    uncheckBillLine,
    uncheckLine,
    startAllocation,
    stopAllocation,
    editAllocation,
    openBillLinesPopup,
    openLinesPopup,
    closeBillLinesPopup,
    closeLinesPopup,
    openLinePopup,
    closeLinePopup,
    resetAll,
    resetAllocations,
    switchTab,
    distributeRemaining,
    searchBillLines,
    searchLines,
    cancelDeallocatingBillLine,
    setDeallocatingBillLine,
    cancelRefutingBillLine,
    setRefutingBillLine,
    cancelConfirmingRefutedBillLine,
    setConfirmingRefutedBillLine,
    toggleRunning,
    updatePurchaseOrder,
    updatePurchaseOrders,
    deletePurchaseOrder,
    setTaxCodes,
    setAccountingAccounts,
    setEditingPurchaseOrder,
    addAccountingAccount,
    updateProjectDocket,
} = actualSlice.actions;

export const selectTotals = (state) => state.actual.totals;
export const selectProjectCurrency = (state) => state.actual.totals.currency;
export const selectProjectDocket = (state) => state.actual.projectDocket;
export const selectLineIds = (state) => state.actual.lineIds;
export const selectLines = (state) => state.actual.actualLines;
export const selectLine = (state, lineId) => state.actual.actualLines[lineId];
export const selectBillLine = (state, billLineId) =>
    state.actual.billLines[billLineId];
export const selectPurchaseOrder = (state, purchaseOrderId) =>
    state.actual.purchaseOrders[purchaseOrderId];
export const selectPurchaseOrders = (state) => state.actual.purchaseOrders;
export const selectToAllocateLines = (state) => state.actual.toAllocateLines;
export const selectToAllocateBillLines = (state) =>
    state.actual.toAllocateBillLines;
export const selectCanMultiCheckLines = (state) =>
    state.actual.toAllocateBillLines.length <= 1;
export const selectCanMultiCheckBillLines = (state) =>
    state.actual.toAllocateLines.length <= 1;
export const selectAllocations = (state) => state.actual.allocations;
export const selectLineIsSearched = (state, lineId) =>
    state.actual.searchedLineIdsSet
        ? lineId in state.actual.searchedLineIdsSet
        : true;
export const selectSearchedLineIdsSet = (state) =>
    state.actual.searchedLineIdsSet;
export const selectBillLineIsSearched = (state, billLineId) =>
    state.actual.searchedBillLineIdsSet
        ? billLineId in state.actual.searchedBillLineIdsSet
        : true;
export const selectLinesPopupOpen = (state) => state.actual.linesPopupOpen;
export const selectBillLinesPopupOpen = (state) =>
    state.actual.billLinesPopupOpen;
export const selectLinePopupOpen = (state) => state.actual.linePopupOpen;
export const selectTab = (state) => state.actual.tab;
export const selectDeallocatingBillLine = (state) =>
    state.actual.deallocatingBillLine;
export const selectRefutingBillLine = (state) => state.actual.refutingBillLine;
export const selectConfirmingRefutedBillLine = (state) =>
    state.actual.confirmingRefutedBillLine;
export const selectChecksAreValid = (state) =>
    state.actual.toAllocateLines.length > 0 &&
    state.actual.toAllocateBillLines.length > 0;
export const selectShowRunning = (state) => state.actual.showRunning;
export const selectTaxCodes = (state) => state.actual.taxCodes;
export const selectAccountingAccounts = (state) =>
    state.actual.accountingAccounts;
export const selectEditingPurchaseOrder = (state) =>
    state.actual.editingPurchaseOrder;

export default actualSlice.reducer;
